/**
 * 账户选择器 —— **可搜索下拉**（2026-10-04 新增）。
 *
 * 依据：**B 域 PRD §5.8**（拼音模糊搜索）· 尤其 **§5.8.4**（展示文案与匹配文本分离）·
 * **`AC-B7.8`**（账户「尾号 1234」等装饰文案**不进入拼音索引** —— 代码走查**必测**）。
 *
 * ## 🔴 为什么需要它（而不是继续用 `TextField select`）
 *
 * 记账页原先四处账户选择都是 MUI `TextField select`（只能点、不能键入）。
 * §5.8 正文写得很直白：「长列表纯点选不可用，『键盘直接键入』是记账效率的硬前提」。
 * 家庭账本到 30 个账户时（v1 生产库实测就是 30），在下拉里找"招商银行"要滚很久。
 *
 * ## 🔴 本组件最要紧的一条：`matchText` 必须**手工构造**
 *
 * PRD §5.8.4 的原话（B 域 PRD 第 669 行）：
 *
 * > 账户显示为「招商银行（尾号 1234）」，但**参与匹配的文本只取「招商银行 1234」**
 * > —— 避免「**尾号**」二字进入拼音首字母串（`zsyhwh1234`）后，
 * > 任何含 `wh` 的输入都会命中该账户，产生噪音候选。
 *
 * ⚠️ 本项目**当前**的账户显示文案是 `{name}（{ownerName}）`（归属人），
 * 还不是「尾号」—— 但**同一个陷阱完全成立**：
 * 归属人「小滟」的拼音首字母 `xy` 一旦进匹配串，
 * 那么**任何** `xy` 开头的输入都会命中这位成员名下的所有账户。
 * ⇒ 匹配串只取 `name`，**归属人是装饰文案**。
 *
 * 🔴 **这是本组件与 `CategoryPicker` 唯一的实质差别**：
 * 分类的展示文案 = 匹配文案（都是中文名），账户的**不是**。
 * 所以 `AccountPicker` 内部**自己构造 `matchText`**，不让调用方有机会漏掉。
 *
 * ## 排序：日常账户在前，信用卡在后
 *
 * 记账时 90% 的笔数落在储蓄卡 / 微信 / 现金上，信用卡是少数。
 * 但**这个顺序不是端侧硬编的** —— 它由调用方传进来的 `accounts` 顺序决定，
 * 本组件**不重排**（与 `CategoryPicker` 一致的纪律：顺序只在一处定）。
 *
 * ## ⚠️ 本组件**不含**什么（诚实登记）
 *
 * - **社保账户**：调用方已过滤（`NewTransactionPage` 的 `dailyAccounts` 注释写明
 *   「社保账户不在这里出现 —— 直接花钱会让人误解」）。本组件不做这层判断。
 * - **跨币种转账**：转账两端币种校验在**纯逻辑层**（`txn-draft.ts`），本组件只是选择器。
 */

import type { AccountView } from '@jiaying/shared';
import { Box } from '@mui/material';

import { SearchSelect } from './SearchSelect.js';
import type { SearchSelectOption } from './SearchSelect.js';

/** 账户选择所需的最小投影（与 `NewTransactionPage` 的 `Option` 同构） */
export type AccountPickerItem = Pick<AccountView, 'id' | 'name'> &
  /**
   * 🆕 批 `115`（T4）：**可选**。
   *
   * 有些调用方（`SettleForm` / `ReimburseDialog` / `LoanRepayDialog`）的候选是
   * **已经拼好的展示文案**（`{ id, label }`），它没有 `ownerName` 结构 ——
   * 让它们把账户再查一遍只会多一次取数、并制造第二份"账户怎么显示"的口径。
   * ⇒ 传 `name` = 要显示的整串文案即可，`ownerName` 缺省就不加括号。
   */
  Partial<Pick<AccountView, 'ownerName' | 'currency'>> & {
    /**
     * 🆕 批 `115`（T4）：**整串展示文案**的覆盖（可选）。
     *
     * `ScheduleDialog` 要显示「名称 · 品类」（中点分隔，不是括号）——
     * 而 `matchText` 仍只取 `name`（§5.8.4：品类是装饰，进匹配串会让"待缴"命中一切待缴账户）。
     */
    readonly label?: string;
  };

/**
 * 🆕 批 `115`（T4）：**带币种**的账户投影。
 *
 * 需要按币种校验的调用方（投资买卖、结算、还款）用它 —— 否则
 * `AccountPickerItem['currency']` 是可选，下游 `TradeContext` 那一类
 * "必须知道账户币种"的类型会拒收（`tsc` 报 `string | undefined` 不可赋给 `string`）。
 */
export type AccountPickerItemWithCurrency = AccountPickerItem & { readonly currency: string };

export interface AccountPickerProps {
  /** 候选账户（**顺序即展示顺序**，本组件不重排） */
  readonly accounts: readonly AccountPickerItem[];
  /** 当前选中项；**空串 = 未选择** */
  readonly value: string;
  readonly onChange: (accountId: string) => void;
  readonly label?: string;
  readonly placeholder?: string;
  readonly error?: boolean;
  readonly helperText?: React.ReactNode;
  /** 是否必选（转账等场景）—— 仅影响文案，不做校验（校验在纯逻辑层） */
  readonly required?: boolean;
  /** 是否允许「（请选择）」空项 —— 转入账户可以为空（表示"还没定"） */
  readonly allowEmpty?: boolean;
  /** 🆕 批 `115`（T4）：提交中 / 结算中等场景要禁止改账户 */
  readonly disabled?: boolean;
  /** 🆕 批 `115`（T4）：紧凑表单里跟同排的 `TextField size="small"` 对齐 */
  readonly size?: 'small' | 'medium';
  /**
   * 🆕 批 `115`（T4）：包一层的 `data-testid`。
   *
   * 🔴 **为什么不直接挂到 `Autocomplete` 的 `inputProps`**：那层类型不接受 `data-*`
   * （铁律 100 的同类坑，`Checkbox` / `Switch` 上已经踩过）⇒ 包一层 `Box` 最稳。
   * 保留旧 `data-testid` 是硬要求：`ReimburseDialog.spec` / `LoanRepayDialog.spec` 靠它。
   */
  readonly testId?: string;
}

/** 下拉项：`SearchSelectOption` 的账户化身 */
export interface AccountOption extends SearchSelectOption {
  /** 归属人（**仅展示**，不进匹配串 —— §5.8.4） */
  readonly ownerName: string;
  readonly currency: string;
}

/**
 * 把账户摊平成一维下拉项。
 *
 * 🔴 **`matchText` 是这里唯一有意义的事**：只取 `name`。
 * 展示文案 `label` 带上归属人（家庭账本里"我的招行"和"太太的招行"必须能区分），
 * 但归属人**绝不能**进匹配串 —— 见文件头 §5.8.4 的说明。
 *
 * 🔴 **为什么导出**（与 `CategoryPicker.toOptions` 同因）：
 * MUI Autocomplete 的弹层在 `renderToStaticMarkup` 下**完全不渲染**
 * （㊳ 批实测 `MuiAutocomplete-option` 命中 0）⇒ 选项内容**必须直接对纯函数测**，
 * 绕道 DOM 只能拿到空列表（**假信号**，见 `test/render.tsx` 升级触发线 ①）。
 */
export function toAccountOptions(accounts: readonly AccountPickerItem[]): AccountOption[] {
  return accounts.map((a) => ({
    id: a.id,
    // 🔴 归属人是**装饰**：有就加括号，没有（候来自已拼好的文案）就原样显示
    label:
      a.label ??
      (a.ownerName === undefined || a.ownerName === ''
        ? a.name
        : `${a.name}（${a.ownerName}）`),
    // 🔴 只取账户名：归属人是**装饰文案**，进匹配串会制造噪音候选（AC-B7.8）
    matchText: a.name,
    ownerName: a.ownerName ?? '',
    currency: a.currency ?? '',
  }));
}

export function AccountPicker({
  accounts,
  value,
  onChange,
  label = '账户',
  placeholder = '支持拼音，如 zsyh / zhaoshang',
  error,
  helperText,
  required = false,
  allowEmpty = false,
  disabled = false,
  size,
  testId,
}: AccountPickerProps) {
  const options = toAccountOptions(accounts);

  const select = (
    <SearchSelect<AccountOption>
      label={required ? `${label} *` : label}
      placeholder={placeholder}
      options={options}
      value={value}
      onChange={onChange}
      error={error}
      helperText={helperText}
      disabled={disabled}
      {...(size === undefined ? {} : { size })}
      noOptionsText={
        allowEmpty ? '无匹配项 —— 也可以先不选，之后再补' : '无匹配项 —— 请检查账户名'
      }
    />
  );

  // 🔴 只在需要时包一层（`data-testid` 不能挂到 Autocomplete 的 input 上，见 `testId` 的说明）
  return testId === undefined ? select : <Box data-testid={testId}>{select}</Box>;
}

export default AccountPicker;
